import { useState, useEffect, useRef } from 'react';
import { X, Plus, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface QuickTagPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
  locationName: string;
  currentTags: string[];
  availableTags: string[];
  position: { x: number; y: number };
  queryKey: string[];
}

export function QuickTagPopover({
  open,
  onOpenChange,
  locationId,
  locationName,
  currentTags,
  availableTags,
  position,
  queryKey
}: QuickTagPopoverProps) {
  const [newTag, setNewTag] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>(currentTags);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Sync selectedTags when currentTags changes
  useEffect(() => {
    setSelectedTags(currentTags);
  }, [currentTags]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Close on escape or click outside
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onOpenChange]);

  const updateTagsMutation = useMutation({
    mutationFn: async (tags: string[]) => {
      const { error } = await supabase
        .from('charging_locations')
        .update({ tags })
        .eq('id', locationId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Tags bijgewerkt');
    },
    onError: () => {
      toast.error('Kon tags niet bijwerken');
    }
  });

  const toggleTag = (tag: string) => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    setSelectedTags(newTags);
    updateTagsMutation.mutate(newTags);
  };

  const addNewTag = () => {
    const trimmed = newTag.trim().toUpperCase();
    if (!trimmed) return;
    if (selectedTags.includes(trimmed)) {
      setNewTag('');
      return;
    }

    const newTags = [...selectedTags, trimmed];
    setSelectedTags(newTags);
    updateTagsMutation.mutate(newTags);
    setNewTag('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addNewTag();
    }
  };

  // Calculate position to keep popover in viewport
  const getAdjustedPosition = () => {
    const popoverWidth = 280;
    const popoverHeight = 300;
    const padding = 16;
    
    let x = position.x;
    let y = position.y;

    // Adjust horizontal position
    if (x + popoverWidth > window.innerWidth - padding) {
      x = window.innerWidth - popoverWidth - padding;
    }
    if (x < padding) x = padding;

    // Adjust vertical position
    if (y + popoverHeight > window.innerHeight - padding) {
      y = position.y - popoverHeight - 10;
    }
    if (y < padding) y = padding;

    return { x, y };
  };

  if (!open) return null;

  const adjustedPos = getAdjustedPosition();
  const allTags = [...new Set([...availableTags, ...selectedTags])].sort();

  return (
    <div 
      className="fixed inset-0 z-50"
      style={{ pointerEvents: 'auto' }}
    >
      <div
        ref={popoverRef}
        className="absolute bg-popover border border-border rounded-lg shadow-lg w-[280px] overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        style={{ 
          left: adjustedPos.x, 
          top: adjustedPos.y,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/50">
          <span className="text-sm font-medium truncate max-w-[200px]">
            Tags: {locationName}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tags grid */}
        <div className="p-3 max-h-[180px] overflow-y-auto">
          {allTags.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              Nog geen tags beschikbaar
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {allTags.map((tag) => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <Badge
                    key={tag}
                    variant={isSelected ? "default" : "outline"}
                    className={cn(
                      "cursor-pointer transition-all select-none",
                      isSelected 
                        ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                        : "hover:bg-muted"
                    )}
                    onClick={() => toggleTag(tag)}
                  >
                    {isSelected && <Check className="h-3 w-3 mr-1" />}
                    {tag}
                  </Badge>
                );
              })}
            </div>
          )}
        </div>

        {/* New tag input */}
        <div className="px-3 pb-3 pt-1 border-t border-border">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              placeholder="Nieuwe tag..."
              value={newTag}
              onChange={(e) => setNewTag(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              className="h-8 text-sm uppercase"
              maxLength={20}
            />
            <Button
              size="sm"
              className="h-8 px-2"
              onClick={addNewTag}
              disabled={!newTag.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
